import React, { useEffect, useState } from "react";
import * as notify from "../../shared/notify";

import Loading from "../../components/callback/loading";

import { getWips, sendAttributeData } from "../../components/attributes/lib/attribute-data";
import { ProductsProvider } from "./index";

const buildDefaultState = (keys, initValue) => {
    return keys.reduce((obj, key) => {
        obj[key] = initValue;

        return obj;
    }, {});
};

const Products = (props) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [products, setProducts] = useState(buildDefaultState(props.codingWipKeys, {}));
    const [bulkRefresh, setBulkRefresh] = useState(false);

    useEffect(() => {
        fetchProducts();
    }, []);

    useEffect(() => {
        if (bulkRefresh) {
            setBulkRefresh(false);
        }
    }, [bulkRefresh]);

    const fetchProducts = async (refreshWipKeys = [], refresh = "", refreshBulk = false) => {
        const productData = props.codingWipKeys.length > 0 ? await getWips(props.codingWipKeys, true) : [];

        const newProductData = productData.reduce((obj, wip) => {
            const currentKey = wip.key;
            obj[currentKey] = wip;
            obj[currentKey]["shouldBulk"] = false;

            if (refreshWipKeys.includes(currentKey)) {
                obj[currentKey]["refresh"] = refresh;
            }

            return obj;
        }, {});
        setProducts(newProductData);

        // TODO: it has to refresh only selected UPCs - currently it will force all products to refresh
        if (refreshBulk) {
            setBulkRefresh(true);
        }

        setIsLoaded(true);
    };

    const update = async (codingWipKey, uuid, value, type) => {
        setProducts({
            ...products,
            [codingWipKey]: { ...products[codingWipKey], [uuid]: value },
        });
    };

    const updateAttributes = async (codingWipKey, payload) => {
        const response = await sendAttributeData({ wipKey: codingWipKey, shipmentKey: props.shipmentKey, sourcePage: "profiling" }, payload);

        if (!response) {
            return { refresh: false, success: false };
        }

        if (response.refresh) {
            fetchProducts();
        } else {
            setProducts({
                ...products,
                [codingWipKey]: { ...products[codingWipKey], ...payload },
            });
        }

        notify.success("Saved");

        return response;
    };

    const bulkUpdate = async (payload) => {
        let bulkCount = 0;

        const productPromises = Object.keys(products).reduce((arr, key) => {
            if (products[key].shouldBulk) {
                arr.push(
                    sendAttributeData({ wipKey: key, shipmentKey: props.shipmentKey, sourcePage: "profiling" }, payload).then((r) => {
                        return {
                            ...products[key],
                            ...payload,
                            shouldBulk: false,
                            refresh: "",
                            forceRefresh: r.refresh || false,
                        };
                    })
                );
                bulkCount = bulkCount + 1;
            } else {
                arr.push(
                    Promise.resolve({
                        ...products[key],
                        shouldBulk: false,
                        refresh: "",
                        forceRefresh: false,
                    })
                );
            }

            return arr;
        }, []);

        return Promise.all(productPromises).then((values) => {
            const refreshWipKeys = [];
            const stamp = Math.round(new Date().getTime() / 1000);

            const newProductData = values.reduce((obj, val) => {
                obj[val.key] = val;

                if (val.forceRefresh) {
                    refreshWipKeys.push(val.key);
                }

                return obj;
            }, {});

            if (bulkCount > 0) {
                if (refreshWipKeys.length > 0) {
                    fetchProducts(refreshWipKeys, stamp, true);
                } else {
                    setProducts(newProductData);
                }

                notify.success("Saved");

                return true;
            }

            return false;
        });
    };

    const setBulkAll = (bool, keys) => {
        const newProductData = Object.keys(products).reduce((obj, key) => {
            if (keys.includes(key)) {
                obj[key] = {
                    ...products[key],
                    shouldBulk: bool,
                };
            } else {
                obj[key] = {
                    ...products[key],
                };
            }

            return obj;
        }, {});

        setProducts(newProductData);
    };

    const refresh = async () => {
        return fetchProducts();
    };

    if (!isLoaded) {
        return <Loading />;
    }

    return (
        <ProductsProvider value={{ products, bulkUpdate, update, updateAttributes, refresh, setBulkAll, bulkRefresh }}>
            {props.children}
        </ProductsProvider>
    );
};

export default Products;
